import { useState, useEffect } from 'react'
import { HomeHeader } from '../home/HomeHeader'
import { useAuth } from '../../contexts/AuthContext'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button } from '../ui/button'
import { BookOpen } from 'lucide-react'
import { apiClient } from '../../lib/api'
import { type LibraryBook } from '../../types'
import BookCard from '../BookCard'
import { Search, type SortOption } from '../shared/Search'
import { Pagination, type PaginationData } from '../shared/Pagination'
import { useTranslation } from 'react-i18next'

export function Library() {
  const { t } = useTranslation('common')
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  
  const [books, setBooks] = useState<LibraryBook[]>([])
  const [loading, setLoading] = useState(true)
  const [topLanguageCodes, setTopLanguageCodes] = useState<string[]>([])
  const [pagination, setPagination] = useState<PaginationData | null>(null)
  
  // Initialize state from URL parameters
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')
  const [languageFilter, setLanguageFilter] = useState<string[]>(() => {
    const langParam = searchParams.get('language')
    return langParam ? langParam.split(',').filter(Boolean) : []
  })
  const [genreFilter, setGenreFilter] = useState(searchParams.get('genre') || '')
  const [originalLanguageFilter, setOriginalLanguageFilter] = useState(searchParams.get('original_language') || '')
  const [sortFilter, setSortFilter] = useState<SortOption | ''>(() => {
    const sort = searchParams.get('sort') || ''
    return (sort === '' || ['popularity', 'title-asc', 'title-desc', 'author-asc', 'author-desc', 'newest'].includes(sort)) 
      ? (sort as SortOption | '') 
      : ''
  })
  const [gradeFilter, setGradeFilter] = useState<string[]>(() => {
    const gradeParam = searchParams.get('grade')
    return gradeParam ? gradeParam.split(',').filter(Boolean) : []
  })

  // Sync state with URL parameters
  useEffect(() => {
    setSearchTerm(searchParams.get('search') || '')
    const langParam = searchParams.get('language')
    setLanguageFilter(langParam ? langParam.split(',').filter(Boolean) : [])
    setGenreFilter(searchParams.get('genre') || '')
    setOriginalLanguageFilter(searchParams.get('original_language') || '')
    const sort = searchParams.get('sort') || ''
    setSortFilter((sort === '' || ['popularity', 'title-asc', 'title-desc', 'author-asc', 'author-desc', 'newest'].includes(sort)) 
      ? (sort as SortOption | '') 
      : '')
    const gradeParam = searchParams.get('grade')
    setGradeFilter(gradeParam ? gradeParam.split(',').filter(Boolean) : [])
  }, [searchParams])
  
  // Load top languages on mount
  useEffect(() => {
    loadTopLanguages()
  }, [])

  // Load books when URL parameters change
  useEffect(() => {
    loadBooks()
  }, [searchParams])

  const loadTopLanguages = async () => {
    try {
      const data = await apiClient.library.getTopLanguages()
      setTopLanguageCodes(data.top_languages || [])
    } catch (error) {
      console.error('Failed to load top languages:', error)
      // Continue with empty array (will use defaults)
    }
  }

  const loadBooks = async () => {
    try {
      setLoading(true)
      // Read filters from URL parameters
      const search = searchParams.get('search') || ''
      const language = searchParams.get('language') || '' // Comma-separated language codes
      const genre = searchParams.get('genre') || ''
      const grade = searchParams.get('grade') || '' // Comma-separated CEFR levels
      const page = parseInt(searchParams.get('page') || '1', 10)
      const pageSize = parseInt(searchParams.get('page_size') || '24', 10)
      
      const filters: { search?: string; language?: string; genre?: string; grade?: string; page: number; page_size: number } = {
        page: page,
        page_size: pageSize
      }
      if (search) filters.search = search
      if (language) filters.language = language // Backend will parse comma-separated codes
      if (genre) filters.genre = genre
      if (grade) filters.grade = grade // Backend will parse comma-separated CEFR levels
      
      const data = await apiClient.getLibraryBooks(filters)
      setBooks(data.books || [])
      setPagination(data.pagination || null)
      
      // Scroll to top when page changes
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (error) {
      console.error('Failed to load library books:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    // Apply all current filters including search term
    handleApplyFilters()
  }
  
  const handleLanguageChange = (language: string | string[]) => {
    // Only update local state - filters applied on "Show Results" click
    const langArray = Array.isArray(language) ? language : []
    setLanguageFilter(langArray)
  }
  
  const handleGenreChange = (genre: string) => {
    setGenreFilter(genre)
  }

  const handleOriginalLanguageChange = (language: string) => {
    setOriginalLanguageFilter(language)
  }

  const handleSortChange = (sort: SortOption | '') => {
    setSortFilter(sort)
  }

  const handleGradeChange = (grades: string[]) => {
    setGradeFilter(grades)
  }

  const handleApplyFilters = () => {
    // Apply all current filter state to URL
    const newParams = new URLSearchParams()
    if (searchTerm) newParams.set('search', searchTerm)
    if (languageFilter.length > 0) newParams.set('language', languageFilter.join(','))
    if (genreFilter) newParams.set('genre', genreFilter)
    if (originalLanguageFilter) newParams.set('original_language', originalLanguageFilter)
    if (sortFilter) newParams.set('sort', sortFilter)
    if (gradeFilter.length > 0) newParams.set('grade', gradeFilter.join(','))
    setSearchParams(newParams)
  }

  const handleClearFilters = () => {
    setLanguageFilter([])
    setGenreFilter('')
    setOriginalLanguageFilter('')
    setSortFilter('')
    setGradeFilter([])
    setSearchTerm('')
    setSearchParams(new URLSearchParams())
  }

  const handlePageChange = (page: number) => {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('page', page.toString())
    setSearchParams(newParams)
  }

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/')
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <HomeHeader user={user} onLogout={handleLogout} />
      
      <div className="container mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-2 h-8 bg-primary rounded-full"></div>
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t('library.discoverBooks')}</h1>
            <p className="text-sm text-muted-foreground">
              {t('library.discoverSubtitle')}
            </p>
          </div>
        </div>

        {/* Search and Filters */}
        <Search
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          onSearchSubmit={handleSearch}
          searchPlaceholder={t('library.searchPlaceholderLibrary')}
          selectedLanguage={languageFilter}
          selectedGenre={genreFilter}
          selectedOriginalLanguage={originalLanguageFilter}
          selectedSort={sortFilter}
          selectedGrades={gradeFilter}
          onLanguageChange={handleLanguageChange}
          onGenreChange={handleGenreChange}
          onOriginalLanguageChange={handleOriginalLanguageChange}
          onSortChange={handleSortChange}
          onGradeChange={handleGradeChange}
          onApplyFilters={handleApplyFilters}
          onClearFilters={handleClearFilters}
          showGenreFilter={true}
          showOriginalLanguageFilter={true}
          showSortFilter={true}
          showGradeFilter={true}
          useMultiSelect={true}
          topLanguageCodes={topLanguageCodes}
        />

        {/* Books Grid */}
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="text-muted-foreground mt-4">Loading books...</p>
          </div>
        ) : books.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">
              No books found
            </h2>
            <p className="text-muted-foreground mb-6">
              {(searchParams.get('genre') || searchParams.get('language') || searchParams.get('search')) 
                ? "We're constantly adding new books! Check out our popular titles below or try different filters."
                : "Check back soon for new books!"}
            </p>
            {(searchParams.get('genre') || searchParams.get('language') || searchParams.get('search')) && (
              <div className="flex gap-3 justify-center flex-wrap mt-4">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchParams(new URLSearchParams())
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                  }}
                >
                  Clear Filters
                </Button>
                {searchParams.get('genre') && (
                  <Button
                    variant="default"
                    onClick={() => navigate('/')}
                  >
                    Browse All Genres
                  </Button>
                )}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6 py-4">
              {books.map((book) => (
                <BookCard 
                  key={book.id} 
                  book={book}
                  variant="library-book"
                />
              ))}
            </div>

            {/* Pagination */}
            {pagination && (
              <div className="mt-8 mb-4">
                <Pagination 
                  pagination={pagination} 
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}

      </div>
    </div>
  )
}
